import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { WaFooter } from '../whatsapp/layout/WaChrome';
import connectLogo from '../primedha connect.png';
import { ANNUAL_MONTHS_BILLED, CONTACT_EMAIL, GST_PERCENT, PLANS, featureLines, formatMoney, priceFor } from './plans';

// The Terms of Service and the Privacy Policy are two sections of one page, public/wa-privacy-policy.html
const POLICY_PAGE = `${process.env.PUBLIC_URL || ''}/wa-privacy-policy.html`;

const CURRENCIES = [
  ['INR', 'My business is in India (₹ + GST)'],
  ['USD', 'My business is elsewhere ($)'],
];
const INTERVALS = [
  ['monthly', 'Monthly'],
  ['annual', `Annual (${12 - ANNUAL_MONTHS_BILLED} months free)`],
];

const mailTo = (subject) => `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}`;

/** A row of buttons of which exactly one is pressed. */
const Toggle = ({ label, options, value, onChange }) => (
  <div className="pricing-toggle-group" role="group" aria-label={label}>
    {options.map(([key, text]) => (
      <button key={key} type="button" aria-pressed={value === key} onClick={() => onChange(key)}
        className={`pricing-toggle ${value === key ? 'active' : ''}`}>
        {text}
      </button>
    ))}
  </div>
);

const PlanCard = ({ plan, currency, interval }) => {
  const price = priceFor(plan, currency, interval);

  let priceBlock;
  if (plan.trial) {
    priceBlock = <p className="pricing-trial-price">Free for 7 days from your first message</p>;
  } else if (price == null) {
    priceBlock = <p className="pricing-price">Contact us</p>;
  } else {
    priceBlock = (
      <>
        <p className="pricing-price">
          {formatMoney(currency, price)}
          <span className="pricing-price-unit"> / {interval === 'monthly' ? 'month' : 'year'}{currency === 'INR' ? ' + GST' : ''}</span>
        </p>
        {interval === 'annual' && (
          <p className="pricing-price-sub">That is {formatMoney(currency, Math.round(price / 12))} a month</p>
        )}
      </>
    );
  }

  return (
    <section className="pricing-card" aria-label={`${plan.title} plan`}>
      <h3 className="pricing-card-title">{plan.title}</h3>
      <div className="pricing-price-block">{priceBlock}</div>
      <ul className="pricing-features">
        {featureLines(plan).map((line) => <li key={line}>{line}</li>)}
      </ul>
      {plan.extraNumber && (
        <p className="pricing-card-note">
          More sender numbers: {formatMoney(currency, plan.extraNumber[currency])}{currency === 'INR' ? ' + GST' : ''} each a month, on request.
        </p>
      )}
      {plan.enterprise && (
        <p className="pricing-card-note">
          Priced per contract. <a href={mailTo('Primedha Connect Enterprise plan')}>Write to us</a> and we will set it up.
        </p>
      )}
    </section>
  );
};

/**
 * The public price list (/pricing): the plans a customer can take, monthly or yearly, in rupees (+ GST) or dollars. Reachable from the
 * login page; no session is needed.
 */
const PricingPage = () => {
  const [currency, setCurrency] = useState('INR');
  const [billing, setBilling] = useState('monthly');

  return (
    <div className="wa-dashboard-page">
      <div className="wa-dashboard-inner">
        <main className="pricing-main">
          <div className="pricing-top">
            <img src={connectLogo} alt="Primedha Connect" className="pricing-logo" />
            <div className="pricing-top-actions">
              <Link to="/login" className="pricing-link-button">Log in</Link>
              <a href={mailTo('Primedha Connect trial')} className="pricing-cta">Start your trial</a>
            </div>
          </div>

          <h1 className="pricing-heading">Plans and prices</h1>
          <p className="pricing-intro">
            Meta bills you directly for the WhatsApp messages you send; Primedha Connect never marks that up. These fees are for the
            platform: your own isolated workspace and database, templates, scheduling, consent tracking, encrypted personal data and
            support. Every plan starts with a free 7-day trial that begins with your first message.
          </p>

          <div className="pricing-toggles">
            <Toggle label="Currency" options={CURRENCIES} value={currency} onChange={setCurrency} />
            <Toggle label="Billing" options={INTERVALS} value={billing} onChange={setBilling} />
          </div>

          <div className="pricing-grid">
            {PLANS.map((plan) => <PlanCard key={plan.tier} plan={plan} currency={currency} interval={billing} />)}
          </div>

          <ul className="pricing-notes">
            <li>Prices exclude GST. Customers in India are invoiced in rupees with {GST_PERCENT}% GST; customers elsewhere are invoiced in US dollars (export of services, no GST).</li>
            <li>A year paid in advance costs {ANNUAL_MONTHS_BILLED} months.</li>
            <li>Messages are the WhatsApp messages you send through the platform. Limits are per workspace: at a limit we ask you to move to the next plan; nothing is charged automatically.</li>
            <li>Support is by e-mail in business hours (India). Plans differ in limits and features, not in a response-time guarantee.</li>
            <li>
              Read the <a href={`${POLICY_PAGE}#terms`} target="_blank" rel="noopener noreferrer">Terms of Service</a> and
              the <a href={`${POLICY_PAGE}#privacy`} target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
            </li>
          </ul>
        </main>

        <WaFooter />
      </div>
    </div>
  );
};

export default PricingPage;
